import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import DocumentManager from "@/components/app/DocumentManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Paperclip } from "@phosphor-icons/react";

/**
 * Generic master data manager driven by a config object.
 * config = { title, overline, subtitle, accent, endpoint, testid,
 *   columns:[{key,label,render?,badge?}], fields:[...], perms:{add,edit,delete},
 *   documentsEntity }
 * fields item: { name,label,type(text|number|email|textarea|date|select|switch),
 *   options:[{value,label}], optionsEndpoint, optionValue, optionLabel, required, section, colSpan }
 */
export default function ResourceManager({ config }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [optionData, setOptionData] = useState({});
  const [docTarget, setDocTarget] = useState(null);
  const [custom, setCustom] = useState({});
  const isOther = (v) => ["other", "others"].includes(String(v ?? "").toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${config.endpoint}`, { params: search ? { q: search } : {} });
      setRows(res.data);
    } catch (e) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [config.endpoint, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // fetch dynamic option sources
  useEffect(() => {
    const sources = [...new Set((config.fields || []).filter((f) => f.optionsEndpoint).map((f) => f.optionsEndpoint))];
    sources.forEach(async (ep) => {
      try {
        const res = await api.get(`/${ep}`);
        setOptionData((prev) => ({ ...prev, [ep]: res.data }));
      } catch {}
    });
  }, [config.fields]);

  const openCreate = () => {
    const init = {};
    (config.fields || []).forEach((f) => {
      init[f.name] = f.type === "switch" ? true : f.type === "number" ? 0 : f.default ?? "";
    });
    setForm(init);
    setEditing(null);
    setCustom({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const init = {};
    const cust = {};
    (config.fields || []).forEach((f) => {
      init[f.name] = row[f.name] ?? (f.type === "switch" ? false : f.type === "number" ? 0 : "");
      if (f.type === "select" && row[f.name]) {
        const opts = optionsFor(f).map((o) => String(o.value));
        if (!opts.includes(String(row[f.name]))) cust[f.name] = true;
      }
    });
    setForm(init);
    setCustom(cust);
    setEditing(row);
    setDialogOpen(true);
  };

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const submit = async () => {
    for (const f of config.fields) {
      if (f.required && (form[f.name] === "" || form[f.name] === null || form[f.name] === undefined)) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const payload = { ...form };
    config.fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0); });
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/${config.endpoint}/${editing.id}`, payload);
        toast.success(`${config.singular} updated`);
      } else {
        await api.post(`/${config.endpoint}`, payload);
        toast.success(`${config.singular} created`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${config.endpoint}/${deleteId}`);
      toast.success(`${config.singular} removed`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  const optionsFor = (f) => {
    if (f.options) return f.options;
    if (f.optionsEndpoint) {
      const data = optionData[f.optionsEndpoint] || [];
      return data.map((d) => ({ value: d[f.optionValue], label: d[f.optionLabel] }));
    }
    return [];
  };

  const sections = [...new Set((config.fields || []).map((f) => f.section || "Details"))];

  return (
    <div>
      <PageHeader
        overline={config.overline}
        title={config.title}
        subtitle={config.subtitle}
        accent={config.accent}
        actions={
          can(config.perms.add) && (
            <Button data-testid={`${config.testid}-add-button`} onClick={openCreate} className="rounded-sm bg-brand hover:bg-brand-dark">
              <Plus size={16} className="mr-1.5" weight="bold" /> New {config.singular}
            </Button>
          )
        }
      />

      <div className="mb-4 relative w-80 max-w-full">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          data-testid={`${config.testid}-search`}
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-sm border-zinc-200"
        />
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50 border-zinc-200">
              {config.columns.map((c) => (
                <TableHead key={c.key} className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c.label}</TableHead>
              ))}
              <TableHead className="text-right text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="font-body">
            {loading ? (
              <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center py-12 text-zinc-400">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center py-12 text-zinc-400">No records yet.</TableCell></TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} data-testid={`${config.testid}-row`} className="border-zinc-100 hover:bg-zinc-50">
                  {config.columns.map((c) => (
                    <TableCell key={c.key} className="text-sm text-zinc-700 py-3">
                      {c.render ? c.render(row) : c.badge ? (
                        <Badge variant="outline" className="rounded-sm border-zinc-300 font-normal">{row[c.key] ?? "—"}</Badge>
                      ) : (row[c.key] ?? "—")}
                    </TableCell>
                  ))}
                  <TableCell className="text-right py-3">
                    <div className="flex items-center justify-end gap-1">
                      {config.documentsEntity && can("documents.view") && (
                        <Button data-testid={`${config.testid}-docs-button`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-zinc-900" onClick={() => setDocTarget(row)}>
                          <Paperclip size={16} />
                        </Button>
                      )}
                      {can(config.perms.edit) && (
                        <Button data-testid={`${config.testid}-edit-button`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-zinc-900" onClick={() => openEdit(row)}>
                          <PencilSimple size={16} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? `Edit ${config.singular}` : `New ${config.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-2">
            {sections.map((sec) => (
              <div key={sec}>
                {sections.length > 1 && <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-3 border-b border-zinc-100 pb-1">{sec}</div>}
                <div className="grid grid-cols-2 gap-4">
                  {config.fields.filter((f) => (f.section || "Details") === sec).map((f) => (
                    <div key={f.name} className={f.type === "textarea" || f.colSpan === 2 ? "col-span-2" : ""}>
                      {f.type === "switch" ? (
                        <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2 h-10">
                          <Label className="text-sm text-zinc-600">{f.label}</Label>
                          <Switch data-testid={`field-${f.name}`} checked={!!form[f.name]} onCheckedChange={(v) => setField(f.name, v)} />
                        </div>
                      ) : (
                        <>
                          <Label className="text-xs text-zinc-500 mb-1.5 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                          {f.type === "select" ? (() => {
                            const opts = optionsFor(f).filter((o) => o.value !== undefined && o.value !== null && o.value !== "");
                            const otherOpt = opts.find((o) => isOther(o.value));
                            const inCustom = !!custom[f.name];
                            return (
                              <>
                                <Select
                                  value={inCustom && otherOpt ? String(otherOpt.value) : (form[f.name] ? String(form[f.name]) : "")}
                                  onValueChange={(v) => {
                                    if (isOther(v)) { setCustom((p) => ({ ...p, [f.name]: true })); setField(f.name, ""); }
                                    else { setCustom((p) => ({ ...p, [f.name]: false })); setField(f.name, v); }
                                  }}>
                                  <SelectTrigger data-testid={`field-${f.name}`} className="rounded-sm border-zinc-200 h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                                  <SelectContent className="rounded-sm">
                                    {opts.map((o) => (
                                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {inCustom && (
                                  <Input data-testid={`field-${f.name}-custom`} value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} placeholder={`Enter ${f.label.toLowerCase()}`} className="rounded-sm border-zinc-200 h-10 mt-2" />
                                )}
                              </>
                            );
                          })() : f.type === "textarea" ? (
                            <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="rounded-sm border-zinc-200" />
                          ) : (
                            <Input
                              data-testid={`field-${f.name}`}
                              type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text"}
                              value={form[f.name] ?? ""}
                              onChange={(e) => setField(f.name, e.target.value)}
                              className="rounded-sm border-zinc-200 h-10"
                            />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            {editing && can(config.perms.delete) && (
              <Button data-testid={`${config.testid}-delete-button`} variant="outline" className="rounded-sm mr-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteId(editing.id)}>
                <Trash size={16} className="mr-1.5" /> Delete
              </Button>
            )}
            <Button variant="outline" className="rounded-sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid={`${config.testid}-submit-button`} onClick={submit} disabled={saving} className="rounded-sm bg-brand hover:bg-brand-dark">
              {saving ? "Saving…" : editing ? "Save changes" : `Create ${config.singular}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this {config.singular}?</AlertDialogTitle>
            <AlertDialogDescription>This performs a soft delete — the record is archived and hidden, not permanently erased.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Documents */}
      {docTarget && (
        <DocumentManager
          open={!!docTarget}
          onOpenChange={(o) => !o && setDocTarget(null)}
          entity={config.documentsEntity}
          entityId={docTarget.id}
          entityName={docTarget[config.columns[0].key]}
        />
      )}
    </div>
  );
}
